import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import Pagination from '../../common/Pagination';
import SocietySectionToolbar from './SocietySectionToolbar';
import SectionFilterSelect from './SectionFilterSelect';
import { firstImageOrPlaceholder, IMAGE_PLACEHOLDER_PATH } from '../../../utils/imageFallback';

const SocietyFreeItems = ({ society, onError }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    type: 'all',
    sort: 'newest'
  });

  // Load free items
  useEffect(() => {
    const loadItems = async () => {
      setLoading(true);
      try {
        // TODO: Implement actual API call to load free items
        setItems([]);
        setTotalPages(1);
        setTotalCount(0);
      } catch (error) {
        console.error('Error loading free items:', error);
        onError('Failed to load free items');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadItems();
    }
  }, [society.id, currentPage, filters, onError, isAuthenticated]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getCategoryColor = (category) => {
    const colors = {
      books: 'bg-blue-100 text-blue-800',
      furniture: 'bg-green-100 text-green-800',
      electronics: 'bg-purple-100 text-purple-800',
      clothing: 'bg-pink-100 text-pink-800',
      toys: 'bg-yellow-100 text-yellow-800',
      others: 'bg-orange-100 text-orange-800',
      services: 'bg-teal-100 text-teal-800'
    };
    return colors[category] || colors.books;
  };

  const getTypeColor = (type) => {
    return type === 'free' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-blue-100 text-blue-800';
  };

  if (!isAuthenticated) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-8 text-center">
        <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Login Required</h3>
        <p className="text-gray-500 mb-4">You need to be logged in to view free and exchange items.</p>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200">
          Login to Continue
        </button>
      </div>
    );
  }

  const activeFilterCount =
    (filters.category !== 'all' ? 1 : 0) +
    (filters.type !== 'all' ? 1 : 0) +
    (filters.sort !== 'newest' ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <SocietySectionToolbar
        title="Free/Exchange Items"
        count={totalCount}
        itemLabel={{ singular: 'item', plural: 'items' }}
        societyName={society.name}
        searchPlaceholder="Search items..."
        searchValue={filters.search}
        onSearchChange={(v) => handleFilterChange({ ...filters, search: v })}
        activeFilterCount={activeFilterCount}
        addButton={
          isAuthenticated
            ? {
                label: 'Post Item',
                onClick: () =>
                  navigate(`/societies/${society.slug}/free/create?society=${society.id}`),
              }
            : null
        }
      >
        <SectionFilterSelect
          label="Category"
          value={filters.category}
          onChange={(e) => handleFilterChange({ ...filters, category: e.target.value })}
        >
          <option value="all">All Categories</option>
          <option value="books">Books</option>
          <option value="furniture">Furniture</option>
          <option value="electronics">Electronics</option>
          <option value="clothing">Clothing</option>
          <option value="toys">Toys</option>
          <option value="others">Others</option>
          <option value="services">Services</option>
        </SectionFilterSelect>
        <SectionFilterSelect
          label="Type"
          value={filters.type}
          onChange={(e) => handleFilterChange({ ...filters, type: e.target.value })}
        >
          <option value="all">All Types</option>
          <option value="free">Free</option>
          <option value="exchange">Exchange</option>
        </SectionFilterSelect>
        <SectionFilterSelect
          label="Sort By"
          value={filters.sort}
          onChange={(e) => handleFilterChange({ ...filters, sort: e.target.value })}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="category">Category A-Z</option>
        </SectionFilterSelect>
      </SocietySectionToolbar>

      {/* Items Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-8">
            <LoadingSpinner size="medium" />
          </div>
        ) : items.length > 0 ? (
          items.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow duration-200">
              {/* Item Image */}
              <div className="h-48 bg-gray-200 flex items-center justify-center">
                <img
                  src={firstImageOrPlaceholder(item.images)}
                  alt={item.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                  }}
                />
              </div>

              {/* Item Info */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                    {item.title}
                  </h3>
                  <div className="flex space-x-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(item.category)}`}>
                      {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                    </span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(item.type)}`}>
                      {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                    </span>
                  </div>
                </div>

                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {item.description}
                </p>

                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    item.is_available 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {item.is_available ? 'Available' : 'Taken'}
                  </span>
                  <span className="text-sm text-gray-500">
                    {new Date(item.created_at).toLocaleDateString()}
                  </span>
                </div>

                <div className="mt-4 flex space-x-2">
                  <button className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200">
                    Contact
                  </button>
                  <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors duration-200">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full bg-white rounded-lg shadow-sm border p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No items yet</h3>
            <p className="text-gray-500 mb-4">Be the first to post a free or exchange item in this society.</p>
            <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200">
              Post First Item
            </button>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default SocietyFreeItems;
